<template>
  <div
    :class="[
      { '--snippet': isSnippet, '--has-more': hasMore, '--rounded': rounded },
      'form-submission-table__container'
    ]"
  >
    <div
      :ref="refId"
      class="form-submission-table__wrapper"
      @click="() => $emit('click')"
    >
      <div
        class="form-submission-table__header p-relative d-flex justify-content-between align-items-center"
      >
        <div v-if="isPresent(submission)" class="submission-info">
          <div class="font-weight-bold table-title">
            {{ submissionTitle }}
          </div>
          <div class="text-grey-60 table-datetime">
            {{ toDate(submission.created_at, "MMM D, YYYY, hh:mm") }}
          </div>
        </div>
        <div
          v-if="assessmentFields.length"
          :class="[
            'submission-score d-flex align-items-center',
            { 'mr-3': showGoToPageButton }
          ]"
        >
          {{ correctAnswers.length }} / {{ assessmentFields.length }}
        </div>

        <v-button
          v-if="showGoToPageButton"
          ghost
          class="p-absolute p-0"
          @click="gotoPage"
        >
          <v-icon icon="chevron_right" size="1.2rem" />
        </v-button>
      </div>

      <table class="w-100 form-submission-table text-left">
        <tbody>
          <div
            v-for="group in groups"
            :key="`title-${group.id}`"
            style="display: contents"
            data-cy="form-submission-group"
          >
            <tr
              class="form-submission-table__group-title form-submission-table__row"
            >
              <td colspan="2">{{ group.title }}:</td>
            </tr>

            <template v-if="isPresent(group.form_fields)">
              <form-submission-row
                class="form-submission-table__row"
                v-for="field in group.form_fields"
                :key="field.id"
                :field="field"
                :field-group="group"
                :values="values"
                :is-missable="isMissable"
                :can-view-correct-answers="canViewCorrectAnswers"
                @input="vs => $emit('input', vs)"
              />
            </template>

            <template v-if="isPresent(group.form_groups)">
              <form-submission-row
                class="form-submission-table__row"
                v-for="group in group.form_groups"
                :key="group.id"
                :group="group"
                :values="values"
                :is-missable="isMissable"
                :can-view-correct-answers="canViewCorrectAnswers"
                @input="updatedValues => $emit('input', updatedValues)"
              />
            </template>
          </div>
        </tbody>
      </table>
    </div>

    <v-button
      v-if="hasMore"
      raised
      light
      small
      class="p-absolute form-submission-table__more-button"
      @click="() => $emit('click')"
    >
      {{ $t("components.formSubmissionTable.viewMore") }}
    </v-button>
  </div>
</template>

<script>
import { isPresent } from "@/lib/utils"
import { toDate } from "@/lib/date-utils"
import { getAssessmentFields } from "@/lib/form-utils"

import FormSubmissionRow from "@/components/FormSubmissionRow"

export default {
  components: {
    FormSubmissionRow
  },

  model: {
    prop: "values",
    event: "input"
  },

  props: {
    submission: { type: Object, default: () => {} },
    groups: { type: Array, required: true },
    values: { type: Object, default: () => ({}) }, // form_field_id => value
    isMissable: { type: Boolean, default: false },
    title: { type: String, default: "" },
    isSnippet: { type: Boolean, default: false },
    rounded: { type: Boolean, default: false },
    canViewCorrectAnswers: { type: Boolean, default: false }
  },

  data() {
    return {
      hasMore: false
    }
  },

  computed: {
    refId() {
      return `submission-table-${this.submission?.id || this._uid}`
    },

    assessmentFields() {
      return getAssessmentFields(this.groups)
    },

    correctAnswers() {
      return this.assessmentFields.filter(
        field =>
          this.values[field.id] &&
          field.correct_answer == this.values[field.id].value
      )
    },

    submissionTitle() {
      return this.submission.title || this.submission.form?.title
    },

    pagePath() {
      return isPresent(this.submission) ? this.submission.page_path : null
    },

    showGoToPageButton() {
      return (
        (this.pagePath && this.pagePath !== this.$route.path) || this.isSnippet
      )
    }
  },

  mounted() {
    if (this.isSnippet) {
      this.$nextTick(() => {
        const submissionTable = this.$refs[this.refId]
        const submissionTableHeight = submissionTable
          ? submissionTable.offsetHeight
          : 0

        this.hasMore = submissionTableHeight > 250
      })
    }
  },

  methods: {
    isPresent,
    toDate,

    gotoPage() {
      if (this.pagePath) {
        this.$router.push(this.pagePath)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables";
@import "@/assets/styles/mixins";

$submission--max-height: 250px;

.form-submission-table__container {
  @include bg-white;

  position: relative;
  border: 1px solid $color-ui--grey-20;
  overflow: hidden;

  &.--snippet,
  &.--rounded {
    border-radius: 0.75rem;
  }

  &.--snippet {
    cursor: pointer;

    &.--has-more {
      .form-submission-table__wrapper {
        max-height: $submission--max-height;
        mask-image: linear-gradient(
          to bottom,
          rgb(255, 255, 255) 0,
          rgba(0, 0, 0, 0) 140%
        );
      }
    }

    .form-submission-table {
      pointer-events: none;
    }
  }

  .post-attachment__more-button {
    position: absolute;
    bottom: $size--4;
    left: 50%;
    margin-left: -52px;
  }

  .form-submission-table__header {
    padding: $size--3 $size--4;

    .table-title {
      font-size: 16px;
      margin-bottom: 5px;
    }

    .table-datetime {
      font-size: 12px;
      font-weight: 600;
    }

    .v-button {
      right: 1rem;
      top: 50%;
      transform: translateY(-50%);
      color: $color-ui--grey-40;
    }

    .submission-score {
      padding: $size--2 $size--4;
      font-size: $font-size--base;
      border-radius: var(--border-radius);
      background-color: $color-ui--grey-20;
      font-weight: bold;
    }
  }

  .form-submission-table {
    border-collapse: collapse;

    &__row {
      vertical-align: top;
      font-size: $font-size--small;
    }

    &__group-title {
      border-top: 1px solid $color-ui--grey-20;
      border-bottom: 1px solid $color-ui--grey-20;
      font-weight: bold;

      td {
        background-color: $color-ui--grey-10;
        width: 100%;
        padding: $size--3 0 $size--3 $size--4;
      }
    }

    &__more-button {
      left: 50%;
      bottom: $size--4;
      font-size: 12px;
      transform: translateX(-50%);
      font-weight: bold;
    }
  }
}
</style>

<style lang="scss">
@import "@/assets/styles/variables";

.form-submission-table__row {
  .space-pre {
    white-space: pre-wrap;
  }
}

.--dark-mode {
  .form-submission-table__container {
    background-color: $color-ui--white !important;
    color: $color-fixed--white !important;
  }

  .form-submission-table__group-title td {
    background-color: $color-fixed--dark-blue !important;
  }
}
</style>
